import { useState } from "react";
import { env } from "@/lib/env";

/**
 * Closed-alpha disclosure banner. Rendered after login (dashboard,
 * wizard, workspace, case-detail) when VITE_ALPHA_MODE=true. Hidden
 * on landing/login.
 *
 * Dismissible via close button — choice persists across sessions in
 * localStorage so daily users aren't bothered after the first read.
 * Toggling VITE_ALPHA_MODE off in production hides the banner
 * regardless of localStorage state.
 */

const STORAGE_KEY = "alpha_banner_dismissed_v1";

export function AlphaBanner() {
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY) === "1",
  );

  if (!env.alphaMode || dismissed) return null;

  const dismiss = () => {
    window.localStorage.setItem(STORAGE_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 text-amber-900">
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-start gap-3 text-xs sm:text-sm">
        <span className="text-base leading-none mt-0.5" aria-hidden>
          🧪
        </span>
        <div className="flex-1 leading-relaxed">
          <span className="font-semibold">Alpha version.</span>{" "}
          Your data may be wiped during early access without warning.
          Do not file the generated PDFs — this is an alpha build for
          testing only, and we accept no responsibility for the
          accuracy or filing-readiness of any document produced here.
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss alpha notice"
          className="text-amber-700 hover:text-amber-900 text-lg leading-none p-1 -m-1 shrink-0"
        >
          ×
        </button>
      </div>
    </div>
  );
}
